import {
  PropsWithChildren,
  ReactElement,
  ReactNode,
  Suspense,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  CircularProgress,
  Dialog,
  DialogProps,
  Paper,
  PaperProps,
  makeStyles,
} from '@material-ui/core';

import classNames from 'classnames';

import {
  SupportingPaneOriginProvider,
  useIsFromSupportingPane,
} from '../SupportingPaneRegion/SupportingPaneRegion';
import { useSupportingPaneOverlay } from '../SupportingPaneRegion/useSupportingPaneOverlay';
import { ariaHideOutside } from './ariaHideOutside';

import supportingPaneStyles from '../SupportingPaneRegion/SupportingPaneOverlay.module.css';
import styles from './Dialog.module.css';

const useStyles = makeStyles({
  paper: {
    transition: 'max-width 225ms ease-in-out 0ms',
  },
  paperFullScreen: {
    paddingBottom: 0,
  },
});

export type State = {
  content: ReactElement | null;
  dialogProps?: Partial<DialogProps>;
  onClose?: () => void;
  disableBackdropClick?: boolean;
  isFromSupportingPane?: boolean;
  stack: ReactElement[];
};

const defaultState: State = {
  content: null,
  dialogProps: {},
  onClose: undefined,
  disableBackdropClick: true,
  isFromSupportingPane: false,
  stack: [],
};

export type ContextType = {
  openDialog: (opts: Partial<State>) => void;
  closeDialog: (
    event?: unknown,
    reason?: 'backdropClick' | 'escapeKeyDown',
  ) => void;

  popDialog: () => void;
};

const EXIT_TIMEOUT = 300;

const context = createContext<ContextType | undefined>(undefined);

export const useDialog = (): ContextType => {
  const _context = useContext(context);
  const isFromSupportingPane = useIsFromSupportingPane();
  const dialogContext = useMemo<ContextType | undefined>(
    () =>
      _context && isFromSupportingPane
        ? {
            ..._context,
            openDialog: opts =>
              _context.openDialog({ ...opts, isFromSupportingPane: true }),
          }
        : _context,
    [_context, isFromSupportingPane],
  );

  if (!dialogContext) {
    throw new Error('Missing `DialogProvider` wrapper.');
  }

  return dialogContext;
};

const LocationListener = ({
  children,
  onChange,
}: PropsWithChildren<{ onChange: () => void }>) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const prevLocationRef = useRef({
    pathname: location.pathname,
    search: location.search,
  });
  const mountedRef = useRef(false);
  const stepParam = searchParams.get('step');

  useEffect(() => {
    if (!stepParam) {
      if (mountedRef.current) {
        const prev = prevLocationRef.current;
        if (
          location.pathname !== prev.pathname ||
          location.search !== prev.search
        ) {
          onChange();
        }
      }
    }
    prevLocationRef.current = {
      pathname: location.pathname,
      search: location.search,
    };
  }, [location, stepParam, onChange]);

  useEffect(() => {
    mountedRef.current = true;
  }, []);

  return <>{children}</>;
};

/**
 * A component that shows a CircularProgress after a delay of 200ms.
 * If the component unmounts before the delay, nothing is shown.
 */
export const DelayedCircularProgress = () => {
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSpinner(true);
    }, 200);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  if (!showSpinner) {
    return null;
  }

  return <CircularProgress />;
};

const SuspensePaper = (props: PaperProps) => {
  return (
    <Suspense fallback={<DelayedCircularProgress />}>
      <Paper {...props} />
    </Suspense>
  );
};

export const DialogProvider = ({ children }: PropsWithChildren) => {
  const classes = useStyles();

  const [
    {
      content,
      dialogProps,
      onClose,
      disableBackdropClick,
      isFromSupportingPane,
    },
    setState,
  ] = useState<State>(defaultState);

  // We use the closing value to avoid empty dialog content before the animation finishes
  const [closing, setClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (!closing) {
      setClosing(true);
      if (onClose) {
        onClose();
      }
    }
  }, [onClose, closing]);

  const closeDialog = useCallback(
    (_event: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick' && disableBackdropClick) {
        return;
      }
      handleClose();
    },
    [handleClose, disableBackdropClick],
  );

  const openDialog = useCallback((opts: Partial<State>) => {
    setClosing(false);
    setState({
      ...defaultState,
      ...opts,
    });
  }, []);

  const popDialog = useCallback(() => {
    setState(state => {
      const stack = [...state.stack];
      const popped = stack.pop() ?? null;
      return {
        ...state,
        content: popped,
        stack,
      };
    });
  }, [setState]);

  const isOpen = useMemo(() => {
    return !!content && !closing;
  }, [content, closing]);

  const [dialogContainer, setDialogContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const hasContent = !!content;
  const { isBesideSupportingPane } = useSupportingPaneOverlay({
    isOpen: hasContent,
    isFromSupportingPane: !!isFromSupportingPane,
  });

  useEffect(() => {
    if (!hasContent || !dialogContainer) return undefined;
    return ariaHideOutside(dialogContainer);
  }, [hasContent, dialogContainer]);

  useEffect(() => {
    // Reset to default state after dialog has closed
    let timeout: ReturnType<typeof setTimeout>;
    if (content && closing) {
      timeout = setTimeout(() => {
        setState(defaultState);
        setClosing(false);
      }, EXIT_TIMEOUT);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [content, closing]);

  const contextValue = useMemo<ContextType>(
    () => ({ openDialog, closeDialog, popDialog }),
    [openDialog, closeDialog, popDialog],
  );
  const providerClasses: DialogProps['classes'] =
    dialogProps?.classes ?? classes;

  return (
    <context.Provider value={contextValue}>
      {createPortal(<div ref={setDialogContainer} />, document.body)}
      <Dialog
        open={isOpen}
        onClose={closeDialog}
        disableEnforceFocus
        disableRestoreFocus
        fullWidth
        maxWidth="sm"
        transitionDuration={{
          exit: EXIT_TIMEOUT,
        }}
        PaperComponent={SuspensePaper}
        data-track-source-anchor="10f4813a27;dialog-provider-dialog" {...dialogProps}
        container={dialogContainer}
        className={classNames(dialogProps?.className, {
          [supportingPaneStyles.besidePane]: isBesideSupportingPane,
          [styles.rootBesidePane]: isBesideSupportingPane,
        })}
        classes={{
          ...providerClasses,
          container: classNames(providerClasses?.container, {
            [styles.containerBesidePane]: isBesideSupportingPane,
          }),
        }}
      >
        {content && (
          <SupportingPaneOriginProvider
            isFromSupportingPane={!!isFromSupportingPane}
          >
            <LocationListener onChange={handleClose} data-track-source-anchor="c0767bee67;dialog-provider-location-listener">
              {content}
            </LocationListener>
          </SupportingPaneOriginProvider>
        )}
      </Dialog>

      {children}
    </context.Provider>
  );
};

export type MockDialogContextType = ContextType;

export type MockDialogProviderProps = {
  children: ReactNode;
  openDialog?: jest.Mock;
  closeDialog?: jest.Mock;
  popDialog?: jest.Mock;
};

export const MockDialogProvider = ({
  children,
  openDialog = jest.fn(),
  closeDialog = jest.fn(),
  popDialog = jest.fn(),
}: MockDialogProviderProps) => {
  const contextValue: MockDialogContextType = {
    openDialog,
    closeDialog,
    popDialog,
  };

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};
